import { useRuntimeConfig } from '#imports'
import { useBreadcrumbs, useProductSearch } from '@shopware/composables'
import type { Schemas } from '#shopware'
import { getProductUrl } from '@shopware/helpers'
import { addSizekickScript } from '~/utils/handle-sizekick-product-data'

/**
 * Sizekick integration composable.
 *
 * All composables (useBreadcrumbs, useProductSearch) are resolved once during
 * component setup so their inject() calls always run in the correct Vue context,
 * avoiding "injectLocal must be called in setup" errors inside callbacks / after awaits.
 */
export function useSizekick () {
    const config = useRuntimeConfig().public
    const { breadcrumbs } = useBreadcrumbs()
    const { search } = useProductSearch()

    function handleSizekickProductData (payload: { product: Schemas['Product'], configurator: Schemas['PropertyGroup'][] }) {
        const { product, configurator } = payload

        const sizes: string[] = []
        configurator.forEach((variant) => {
            if (variant.id === config.productPropertyGroupSize || variant.id === config.productPropertyGroupClothingSize) {
                variant?.options?.forEach((size) => {
                    sizes.push(size.name)
                })
            }
        })

        const media: string[] = []
        if (product.media && product.media.length >= 1) {
            product.media.forEach((medium) => {
                media.push(medium.media?.url)
            })
        } else if (product?.media?.[0]?.media?.path) {
            media.push(product.media[0].media.path)
        }

        window.sizekickProductData = {
            id: product.parentId,
            name: product.name,
            brand: product.manufacturer?.name,
            gender: product.customFields?.gender,
            category: breadcrumbs?.value?.at(-1)?.name ?? 'home',
            sizes,
            cover: media[0],
            images: media,
            description: product.description,
            url: getProductUrl(product),
            gtins: product.ean,
        }
        addSizekickScript()
    }

    async function handleSizekickOrderData (lineItems: Schemas['LineItem'][] | Schemas['OrderLineItem'][]) {
        const sizekickRelevantProducts: Array<{
            product_id: string | undefined
            product_size: string | undefined
        }> = []

        for (const item of lineItems) {
            if (!item.referencedId) return
            const variant: Schemas['ProductDetailResponse'] = await search(item.referencedId)

            const sizeOptionName = variant?.configurator?.find(
                (option) =>
                    option.id === config.productPropertyGroupSize || option.id === config.productPropertyGroupClothingSize
            )?.name
            if (sizeOptionName) {
                sizekickRelevantProducts.push({
                    product_id: item.payload?.parentId,
                    product_size: item.payload?.options?.find((option) => option.group === sizeOptionName)?.option
                })
            }
        }

        window.sizekickProductData = sizekickRelevantProducts
        addSizekickScript()
    }

    return {
        handleSizekickProductData,
        handleSizekickOrderData,
    }
}
